import type { AiModelSummary, ModelTierDefaultResponse, ReasoningConfig, ReasoningEffort } from "@/api";

/** Canonical order for every reasoning-effort list (MHS-669; matches frontend/). */
export const REASONING_EFFORTS: ReasoningEffort[] = ["none", "minimal", "low", "medium", "high", "xhigh", "max"];

const TIER_PREFIX = "tier:";
/** "Choose for me": the model router picks per request, so there is no thinking level to set. */
export const ROUTER_TIER = `${TIER_PREFIX}router`;

/**
 * The reasoning config of the model that will actually answer: the agent's
 * pinned model when an agent answers, otherwise the chat's model. Tier
 * references (`tier:thorough`) resolve through the tier defaults, as the
 * backend does. The router tier never has one (it picks a model per request).
 */
export function answeringReasoningConfig(
  modelId: string | null | undefined,
  models: readonly AiModelSummary[],
  tierDefaults: readonly ModelTierDefaultResponse[],
): ReasoningConfig | null {
  if (!modelId || modelId === ROUTER_TIER) return null;
  const resolved = modelId.startsWith(TIER_PREFIX)
    ? tierDefaults.find((td) => `${TIER_PREFIX}${td.tier}` === modelId)?.modelId
    : modelId;
  return models.find((m) => m.id === resolved)?.reasoningConfig ?? null;
}

/** The efforts the model accepts, in canonical order. Empty = the model has no thinking control. */
export function supportedEfforts(config: ReasoningConfig | null | undefined): ReasoningEffort[] {
  return REASONING_EFFORTS.filter((e) => config?.supportedEfforts.includes(e));
}

/** A stored effort the model still accepts, else null (= provider default). */
export function effectiveEffort(
  effort: string | null | undefined,
  config: ReasoningConfig | null | undefined,
): ReasoningEffort | null {
  return effort && supportedEfforts(config).includes(effort as ReasoningEffort) ? (effort as ReasoningEffort) : null;
}
